import React from 'react';
import { ArrowUpRight, Check, Sparkles } from 'lucide-react';
import { peptidePrograms, peptideAddOns, businessInfo } from '../data/mock';
import useReveal from '../hooks/useReveal';
import WaitlistCapture from './WaitlistCapture';

const ProgramCard = ({ program, idx }) => {
  const { ref, visible } = useReveal();
  const isFlagship = program.flagship;
  const isElite = program.elite;

  const accentText = isElite ? 'text-platinum' : isFlagship ? 'text-blush' : 'text-ivory';
  const accentBorder = isElite
    ? 'border-platinum/30'
    : isFlagship
      ? 'border-blush/40'
      : 'border-ivory/20';
  const bgTone = isElite ? 'bg-[#0d0d0f]' : isFlagship ? 'bg-carbon' : 'bg-[#101013]';

  return (
    <article
      ref={ref}
      data-testid={`peptide-program-${program.id}`}
      className={`reveal ${visible ? 'is-visible' : ''} relative ${bgTone} border ${accentBorder} p-8 lg:p-10 flex flex-col h-full transition-colors duration-500 hover:border-blush/60`}
      style={{ '--reveal-delay': `${idx * 110}ms` }}
    >
      {/* Ribbon */}
      {isFlagship && (
        <div className="absolute -top-3 left-8">
          <span
            className="text-onyx text-[10px] tracking-[0.28em] uppercase px-4 py-1.5 font-medium"
            style={{
              background: 'linear-gradient(105deg, #FCEEF3 0%, #F0B8CC 28%, #EC8AAE 60%, #D85288 100%)',
            }}
          >
            Most Selected
          </span>
        </div>
      )}
      {isElite && (
        <div className="absolute -top-3 left-8">
          <span className="bg-onyx text-platinum text-[10px] tracking-[0.28em] uppercase px-4 py-1.5 border border-platinum/40 font-medium inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3" />
            VIP Tier
          </span>
        </div>
      )}

      {/* Embossed corner mark */}
      <div className="absolute top-6 right-6 opacity-30">
        <div className="text-[9px] tracking-[0.32em] uppercase text-platinum font-serif italic">
          Privé
        </div>
        <div className="h-px w-8 bg-platinum/40 mt-1 ml-auto" />
      </div>

      {/* Header */}
      <div className="mb-2">
        <p className="eyebrow text-silverx mb-4">
          <span className="inline-block w-6 h-px bg-silverx align-middle mr-2" />
          Peptide Program
        </p>
        <h3 className={`font-serif text-3xl lg:text-[34px] leading-tight ${accentText}`}>
          {program.name.split(' ').slice(0, -1).join(' ')}{' '}
          <span className="brand-italic chrome-rose">{program.name.split(' ').slice(-1)}</span>
        </h3>
        <p className="text-platinum/80 text-sm mt-3 italic font-serif">{program.tagline}</p>
      </div>

      <div className="hairline my-7" />

      {/* Price */}
      <div className="mb-7">
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-5xl lg:text-6xl chrome-rose tracking-tight">
            ${program.price}
          </span>
          <span className="text-platinum/70 text-sm tracking-[0.2em] uppercase">/ month</span>
        </div>
        <p className="text-[11px] tracking-[0.2em] uppercase text-silverx mt-3">
          Monthly program · consultation required
        </p>
      </div>

      <div className="hairline mb-7" />

      {/* Description */}
      <p className="text-platinum text-sm leading-relaxed mb-7">{program.description}</p>

      {/* Inclusions */}
      <p className="eyebrow text-silverx mb-4">Includes</p>
      <ul className="space-y-3 mb-7">
        {program.inclusions.map((item) => (
          <li key={item} className="flex items-start gap-3 text-platinum text-sm leading-relaxed">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${accentText}`} strokeWidth={1.5} />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {/* Bonus block (Signature + Elite only) */}
      {program.bonus && (
        <>
          <p className="eyebrow chrome-rose mb-4">Bonus</p>
          <ul className="space-y-3 mb-7">
            {program.bonus.map((item) => (
              <li key={item} className="flex items-start gap-3 text-platinum text-sm leading-relaxed">
                <Sparkles className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-blush" strokeWidth={1.5} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Best for */}
      <p className="eyebrow text-silverx mb-4">Best For</p>
      <ul className="space-y-2 mb-9 flex-1">
        {program.bestFor.map((item) => (
          <li key={item} className="text-platinum/80 text-sm leading-relaxed italic">
            · {item}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href={businessInfo.bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`peptide-cta-${program.id}`}
        className={`btn-pill group/btn w-full inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full border transition-all duration-300 ${
          isFlagship
            ? 'bg-ivory text-onyx border-ivory hover:bg-blush hover:border-blush'
            : isElite
              ? 'bg-transparent text-ivory border-platinum/60 hover:border-blush hover:text-blush'
              : 'bg-transparent text-ivory border-ivory/30 hover:border-blush hover:text-blush'
        }`}
      >
        <span className="text-[11px] tracking-[0.28em] uppercase">Book Consultation</span>
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:rotate-45" />
      </a>
    </article>
  );
};

const PeptidePrograms = () => {
  const headerReveal = useReveal();
  const addOnsReveal = useReveal();

  return (
    <section
      id="peptides"
      data-testid="peptide-programs-section"
      className="relative py-28 md:py-36 bg-carbon overflow-hidden grain"
    >
      {/* Soft radial glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(circle at 50% 0%, rgba(236,138,174,0.08) 0%, transparent 55%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div
          ref={headerReveal.ref}
          className={`reveal ${headerReveal.visible ? 'is-visible' : ''} max-w-3xl mx-auto text-center mb-16`}
        >
          <p className="eyebrow chrome-rose mb-6">
            <span className="inline-block w-8 h-px bg-blush align-middle mr-3" />
            Get Snatched This Summer
            <span className="inline-block w-8 h-px bg-blush align-middle ml-3" />
          </p>
          <h2 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] text-balance mb-6">
            Peptide Wellness <span className="brand-italic chrome-rose">Programs.</span>
          </h2>
          <p className="text-platinum text-base md:text-lg leading-relaxed">
            Monthly programs designed to keep you consistent, accountable, and focused
            on your transformation. Professional administration, luxury experience, and
            results-driven cadence — built for the woman who treats wellness like the
            commitment it is.
          </p>
        </div>

        {/* Program Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-8 items-stretch mb-16">
          {peptidePrograms.map((program, idx) => (
            <ProgramCard key={program.id} program={program} idx={idx} />
          ))}
        </div>

        {/* Founding Client Waitlist */}
        <div className="mb-20">
          <WaitlistCapture />
        </div>

        {/* Add-Ons */}
        <div
          ref={addOnsReveal.ref}
          className={`reveal ${addOnsReveal.visible ? 'is-visible' : ''} bg-onyx border border-pewter/70 p-9 md:p-12`}
        >
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <p className="eyebrow mb-3">Add-Ons</p>
              <h3 className="font-serif text-ivory text-2xl md:text-3xl leading-tight text-balance">
                Customize your program with{' '}
                <span className="brand-italic chrome-rose">à la carte enhancements.</span>
              </h3>
            </div>
            <p className="text-silverx text-[11px] tracking-[0.22em] uppercase max-w-xs">
              Stackable with any Peptide Program tier
            </p>
          </div>

          <ul className="divide-y divide-pewter/60">
            {peptideAddOns.map((addon) => (
              <li
                key={addon.name}
                className="flex items-baseline justify-between gap-4 py-5"
                data-testid={`peptide-addon-${addon.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              >
                <span className="font-serif text-ivory text-base md:text-lg">{addon.name}</span>
                <span className="font-serif chrome-rose text-base md:text-lg whitespace-nowrap">
                  {addon.price}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer */}
        <p className="text-silverx/70 text-[11px] tracking-[0.18em] uppercase text-center mt-12 max-w-3xl mx-auto leading-relaxed">
          Peptide programs are wellness consultations. All clients receive a personalized
          assessment before any administration begins. Not intended to diagnose, treat,
          cure, or prevent any disease.
        </p>
      </div>
    </section>
  );
};

export default PeptidePrograms;
